import type { EnhancedScopeData } from '@ardoq/data-model';
import { type ArdoqId, type GroupingRule } from '@ardoq/api-types';
import {
  buildGroups,
  getGraphInterfaceForEnhancedScopeData,
  getResultingReferenceList,
  type RawGraphNode2,
} from '@ardoq/graph';
import { groupingRuleOperations } from '@ardoq/core';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import {
  ensureContrast,
  getLightenedColor,
  getShadedColor,
  rgbaObjToStr,
  unknownColorTypeStrToRgbaObj,
} from '@ardoq/color-helpers';
import { colors } from '@ardoq/design-tokens';
import { getIcon, isSvgIconName } from '@ardoq/icons';
import { isTruthy } from '@ardoq/common-helpers';
import type { I18nLocale } from '@ardoq/i18n';
import {
  type Edge,
  type GraphViewProps,
  type Layout,
  type LabelStyle,
  type Node,
  defaultCompactDiskLayoutOptions,
  defaultComponent,
  defaultHierarchicalLayoutData,
  defaultHierarchicalLayoutOptions,
  defaultLayout,
  defaultNodeLabelStyle,
  defaultOrganicLayoutData,
  defaultOrganicLayoutOptions,
  defaultOrthogonalLayoutOptions,
  defaultTreeLayoutOptions,
} from '@ardoq/graph-view';
import type { ComponentRepresentationDataWithColorShades } from '@ardoq/basic-components';
import { wrapLeafNodeLabel } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/graphBuilder/wrapLeafNodeLabel';
import type { ModelIdStyle } from 'tabview/assistantShared/buildStyleByModelId';
import type { AssistantDiagramRecipe } from './recipe';
import { toEdgeStyle } from './references';
import {
  CARD_WIDTH_PX,
  type ComponentCard,
  drawComponentCard,
  layoutComponentCard,
} from './componentCard';
import {
  GROUP_INSETS,
  type GroupContainer,
  type CollapsedComponentGroupCard,
  MIN_GROUP_SIZE,
  drawGroupContainer,
  drawCollapsedGroupHeader,
  drawCollapsedComponentGroupCard,
  layoutCollapsedComponentGroup,
} from './groupContainer';

// Layouts that nest groups as non-overlapping containers, where a solid canonical
// group container reads well. Overlap-prone layouts (organic, circular, radial,
// compact-disk, orthogonal) keep the translucent shape so intersecting groups stay
// legible.
const NESTING_LAYOUT_TYPES: ReadonlySet<string> = new Set([
  'hierarchical',
  'tree',
]);

/**
 * The props `applyRecipe` produces for `<GraphView />`. The remaining GraphView
 * props (event handlers, selection) are supplied by the view component.
 */
export type AssistantDiagramGraphProps = Pick<
  GraphViewProps,
  'nodes' | 'edges' | 'layout' | 'component' | 'collapsedGroupIds'
>;

// Neutral fill for groups that don't represent a component or type (WORKSPACE,
// FIELD, TAG, ...), matching the block diagram's group default (GroupVisual.ts).
const NEUTRAL_COLOR = colors.grey50;

const EDGE_BUNDLING_STRENGTH = 0.95;

const bundlingStrength = (bundleEdges: boolean): number =>
  bundleEdges ? EDGE_BUNDLING_STRENGTH : 0;

// Each layout receives only the options it supports (the recipe's tagged union
// guarantees layout-specific options are present only on the matching layout).
const toAlgorithm = ({
  layout,
}: AssistantDiagramRecipe): Layout['algorithm'] => {
  switch (layout.type) {
    case 'organic':
      return {
        type: 'organic',
        data: defaultOrganicLayoutData,
        options: defaultOrganicLayoutOptions,
      };
    case 'circular':
      return {
        type: 'circular',
        options: { edgeBundling: bundlingStrength(layout.bundleEdges) },
      };
    case 'orthogonal':
      return {
        type: 'orthogonal',
        options: defaultOrthogonalLayoutOptions,
      };
    case 'tree':
      return { type: 'tree', options: defaultTreeLayoutOptions };
    case 'radial':
      return {
        type: 'radial',
        options: { edgeBundling: bundlingStrength(layout.bundleEdges) },
      };
    case 'compact-disk':
      return { type: 'compact-disk', options: defaultCompactDiskLayoutOptions };
    case 'hierarchical':
      return {
        type: 'hierarchical',
        data: {
          ...defaultHierarchicalLayoutData,
          groupEdges: layout.groupEdges,
        },
        options: {
          ...defaultHierarchicalLayoutOptions,
          layoutOrientation: layout.orientation,
          routingStyle: layout.edgeRouting,
        },
      };
  }
};

const toLayout = (recipe: AssistantDiagramRecipe): Layout => ({
  ...defaultLayout,
  algorithm: toAlgorithm(recipe),
});

const labelStyle = (color: string, icon?: string): LabelStyle => ({
  ...defaultNodeLabelStyle,
  textFill: getShadedColor(color),
  ...(icon && isSvgIconName(icon)
    ? { icon: { type: 'svg-sprite', name: icon, color: getShadedColor(color) } }
    : {}),
});

// Non-optional icon and value, so it also serves as the fallback for the card
// fields that require them.
const EMPTY_REPRESENTATION = {
  icon: getIcon(null),
  isImage: false,
  value: '',
} as const satisfies ModelIdStyle['representationData'];

// The color shades a component card/stripe needs, derived from its fill color. The base
// representation (icon/image) comes from `buildStyleByModelId`, already honouring
// per-component overrides; here we only layer the color-dependent shades on top.
const withColorShades = (
  representationData: ModelIdStyle['representationData'],
  color: string
): ComponentRepresentationDataWithColorShades => {
  const lightenedColor = getLightenedColor(color);
  const shadedColor = getShadedColor(color);
  return {
    ...representationData,
    icon: representationData.icon ?? EMPTY_REPRESENTATION.icon,
    value: representationData.value ?? EMPTY_REPRESENTATION.value,
    color,
    lightenedColor,
    shadedColor,
    contrastColor: ensureContrast(lightenedColor, shadedColor),
  };
};

// The icon name a component/type group renders in its header label, or undefined when it
// has none (image-backed representation, or a non-component group with no style).
const labelIconName = (style: ModelIdStyle | undefined): string | undefined =>
  style && !style.representationData.isImage
    ? (style.representationData.value ?? undefined)
    : undefined;

/**
 * Group rendered as the canonical Ardoq group container (canvas): a grey body with
 * a type-colored header stripe, children laid out below via {@link GROUP_INSETS}.
 * Used for nesting layouts; overlap-prone layouts use {@link buildGroupNodeFromRaw}.
 */
const buildGroupContainerNode = (
  styleByModelId: Map<ArdoqId, ModelIdStyle>,
  rawGroup: RawGraphNode2
): Node => {
  const style = rawGroup.modelId
    ? styleByModelId.get(rawGroup.modelId)
    : undefined;
  const color = style?.color ?? NEUTRAL_COLOR;
  const label = rawGroup.label ?? rawGroup.name ?? '';

  if (!style) {
    const container: GroupContainer = { color, label };
    return {
      id: rawGroup.id,
      ...(rawGroup.parentId ? { parentId: rawGroup.parentId } : {}),
      labels: [],
      style: {
        type: 'canvas-group',
        size: MIN_GROUP_SIZE,
        insets: GROUP_INSETS,
        render: (ctx, layout) => drawGroupContainer(ctx, layout, container),
        // Non-component groups collapse to a header-only bar.
        renderCollapsed: (ctx, layout) =>
          drawCollapsedGroupHeader(ctx, layout, container),
        collapsedSize: MIN_GROUP_SIZE,
      },
    };
  }

  const representation = withColorShades(style.representationData, color);
  const container: GroupContainer = {
    color,
    label,
    representation: {
      icon: representation.icon,
      isImage: representation.isImage,
      imageValue: representation.value,
    },
  };
  const labelLines = wrapLeafNodeLabel(label);
  const { height, shapeY } = layoutCollapsedComponentGroup(labelLines);
  const collapsedCard: CollapsedComponentGroupCard = {
    representation,
    color,
    labelLines,
    shapeY,
  };
  return {
    id: rawGroup.id,
    ...(rawGroup.parentId ? { parentId: rawGroup.parentId } : {}),
    labels: [],
    style: {
      type: 'canvas-group',
      size: MIN_GROUP_SIZE,
      insets: GROUP_INSETS,
      render: (ctx, layout) => drawGroupContainer(ctx, layout, container),
      // Component groups collapse to the canonical Modernized Block Diagram card.
      renderCollapsed: (ctx, layout) =>
        drawCollapsedComponentGroupCard(ctx, layout, collapsedCard),
      collapsedSize: { width: CARD_WIDTH_PX, height },
    },
  };
};

const GROUP_FILL_ALPHA = 0.125;
export const translucentGroupFill = (color: string): string =>
  rgbaObjToStr({
    ...unknownColorTypeStrToRgbaObj(getLightenedColor(color)),
    alpha: GROUP_FILL_ALPHA,
  });

const buildGroupNodeFromRaw = (
  styleByModelId: Map<ArdoqId, ModelIdStyle>,
  rawGroup: RawGraphNode2
): Node => {
  const style = rawGroup.modelId
    ? styleByModelId.get(rawGroup.modelId)
    : undefined;
  const color = style?.color ?? NEUTRAL_COLOR;
  const label = rawGroup.label ?? rawGroup.name ?? '';
  return {
    id: rawGroup.id,
    ...(rawGroup.parentId ? { parentId: rawGroup.parentId } : {}),
    labels: [
      {
        text: label,
        style: labelStyle(color, labelIconName(style)),
        layout: { type: 'interior', position: 'top-left' },
      },
    ],
    style: {
      type: 'shape',
      shape: 'round-rectangle',
      fill: translucentGroupFill(color),
      stroke: {
        line: 'solid',
        color: getShadedColor(color),
        thickness: 1,
      },
    },
  };
};

const buildComponentCardNode = (
  scopeData: EnhancedScopeData,
  styleByModelId: Map<ArdoqId, ModelIdStyle>,
  componentId: string,
  nodeId: string,
  parentId: string | undefined
): Node => {
  const component = scopeData.componentsById[componentId];
  const style = styleByModelId.get(componentId);
  const color = style?.color ?? NEUTRAL_COLOR;
  const representation = withColorShades(
    style?.representationData ?? EMPTY_REPRESENTATION,
    color
  );
  const labelLines = wrapLeafNodeLabel(component?.name ?? 'Unknown');
  const { height, shapeY } = layoutComponentCard(labelLines);
  const card: ComponentCard = {
    icon: representation.icon,
    isImage: representation.isImage,
    imageValue: representation.value,
    color,
    labelLines,
    shapeY,
  };
  return {
    id: nodeId,
    labels: [],
    style: {
      type: 'canvas',
      size: { width: CARD_WIDTH_PX, height },
      render: (ctx, layout) => drawComponentCard(ctx, layout, card),
    },
    ...(parentId ? { parentId } : {}),
  };
};

/**
 * Applies a recipe to the in-view graph (`enhancedScopeData`) to produce
 * `@ardoq/graph-view` props. Nodes are the in-view components colored/grouped per
 * the recipe and active grouping rules; edges are the references between them.
 * yFiles computes the layout. Grouping rules follow the same pipeline as block
 * diagram and timeline (`buildGroups`), so all group types are supported.
 */
export const applyRecipe = (
  scopeData: EnhancedScopeData,
  styleByModelId: Map<ArdoqId, ModelIdStyle>,
  recipe: AssistantDiagramRecipe,
  groupingRules: GroupingRule[],
  i18nLocale: I18nLocale
): AssistantDiagramGraphProps => {
  const componentIds = [...scopeData.scopeComponentIds];

  // Only in-scope references: both endpoints must be scope components.
  const inScopeReferenceIds = scopeData.references
    .filter(
      r =>
        scopeData.scopeComponentIds.has(r.source) &&
        scopeData.scopeComponentIds.has(r.target)
    )
    .map(r => r._id);

  // Discard rules that are missing required attributes (e.g. a FIELD rule with
  // no fieldId), matching the behaviour of other views via getActiveGroupingAttributes.
  const validGroupingRules = groupingRules.filter(
    groupingRuleOperations.isValidRule
  );

  const graphInterface = getGraphInterfaceForEnhancedScopeData({
    enhancedScopeData: scopeData,
    conditionalFormatting: [],
    groupingRules: validGroupingRules,
    i18nLocale,
  });

  const {
    groupMap,
    componentMap,
    processedReferences,
    componentGroupsMap,
    clonedComponentsMap,
  } = buildGroups({
    graphInterface,
    graph: scopeData.graphData,
    componentIds,
    referenceIds: inScopeReferenceIds,
    groupingRules: validGroupingRules,
    canBeUsedAsGroup: () => true,
    isParentRelationAsReference: false,
    useNewGrouping: false,
    i18nLocale,
  });

  const renderGroupsAsContainers = NESTING_LAYOUT_TYPES.has(recipe.layout.type);
  const groupNodes = [...groupMap.values()].map(rawGroup =>
    renderGroupsAsContainers
      ? buildGroupContainerNode(styleByModelId, rawGroup)
      : buildGroupNodeFromRaw(styleByModelId, rawGroup)
  );

  const componentNodes = [...componentMap.values()].map(rawItem =>
    buildComponentCardNode(
      scopeData,
      styleByModelId,
      rawItem.modelId ?? rawItem.id,
      rawItem.id,
      rawItem.parentId
    )
  );

  const referenceList = getResultingReferenceList({
    traversedReferenceIds: inScopeReferenceIds,
    processedReferences,
    parentReferenceIds: new Set(),
    componentGroupsMap,
    clonedComponentsMap,
    graph: scopeData.graphData,
  });

  const edges: Edge[] = referenceList
    .map(({ id, modelId, sourceId, targetId }) => {
      const ref = scopeData.referencesById[modelId];
      if (!ref) return null;
      return {
        id,
        source: sourceId,
        target: targetId,
        labels: [],
        style: toEdgeStyle(
          enhancedScopeDataOperations.getReferenceType(scopeData, ref)
        ),
      };
    })
    .filter(isTruthy);

  return {
    nodes: [...groupNodes, ...componentNodes],
    edges,
    layout: toLayout(recipe),
    component: defaultComponent,
  };
};
